import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { storyTypes } from '../data/mock';
import { Send, CheckCircle, AlertCircle, Phone, Mail, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubmitStoryPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    storyType: '',
    partnerWilling: '',
    story: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.storyType) newErrors.storyType = 'Please select a story type';
    if (!formData.partnerWilling) newErrors.partnerWilling = 'Please select an option';
    if (!formData.story.trim()) newErrors.story = 'Please tell us your story';
    else if (formData.story.trim().length < 50) newErrors.story = 'Story must be at least 50 characters';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      await axios.post(`${API}/stories`, formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting story:', error);
      setSubmitError('Failed to submit your story. Please try again.');
    }
    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-4">Thanks for submitting!</h1>
          <p className="text-gray-600 mb-6">
            We've received your story. Our team will review it and may reach out to you from a <strong>252 area code</strong>. Please answer or respond if you see a call or text from that area code.
          </p>
          <button
            onClick={() => {
              setIsSubmitted(false);
              setFormData({
                name: '',
                email: '',
                phone: '',
                storyType: '',
                partnerWilling: '',
                story: ''
              });
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Submit Another Story
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Header */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <FileText size={40} className="text-white" />
            <h1 className="text-4xl md:text-5xl font-black text-white">
              Submit Your Story
            </h1>
          </div>
          <p className="text-red-100 text-lg max-w-2xl">
            Share your experience with us. Your story could be featured on our show or podcast.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 flex items-start gap-3">
            <AlertCircle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-semibold">Important Notice</p>
              <p className="text-amber-700 text-sm mt-1">
                Only submit your story if you are willing to be on the podcast or show. Do NOT submit a story if you are unwilling to be called. We will reach out to you via a <strong>252 area code</strong>.
              </p>
            </div>
          </div>

          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 flex items-start gap-3">
              <AlertCircle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{submitError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Tell Us Your Story</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className={`w-full px-4 py-3 border rounded-lg outline-none transition-all ${
                    errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'
                  } focus:ring-2 focus:border-red-500`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Mail size={16} className="inline mr-1" /> Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={`w-full px-4 py-3 border rounded-lg outline-none transition-all ${
                    errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'
                  } focus:ring-2 focus:border-red-500`}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone size={16} className="inline mr-1" /> Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(123) 456-7890"
                  className={`w-full px-4 py-3 border rounded-lg outline-none transition-all ${
                    errors.phone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'
                  } focus:ring-2 focus:border-red-500`}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* Story Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What type of story do you have? *
                </label>
                <select
                  name="storyType"
                  value={formData.storyType}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg outline-none transition-all bg-white ${
                    errors.storyType ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'
                  } focus:ring-2 focus:border-red-500`}
                >
                  <option value="">Select one...</option>
                  {storyTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.storyType && <p className="text-red-500 text-sm mt-1">{errors.storyType}</p>}
              </div>
            </div>

            {/* Partner Willing */}
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Is your partner willing to be on the show? *
              </label>
              <div className="flex flex-wrap gap-4">
                {['Yes', 'No', 'Not Sure'].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center gap-2 px-5 py-3 border rounded-lg cursor-pointer transition-all ${
                      formData.partnerWilling === option
                        ? 'border-red-600 bg-red-50 text-red-600'
                        : 'border-gray-300 hover:border-red-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="partnerWilling"
                      value={option}
                      checked={formData.partnerWilling === option}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.partnerWilling === option ? 'border-red-600' : 'border-gray-400'
                    }`}>
                      {formData.partnerWilling === option && (
                        <span className="w-2 h-2 rounded-full bg-red-600" />
                      )}
                    </span>
                    <span className="font-medium">{option}</span>
                  </label>
                ))}
              </div>
              {errors.partnerWilling && <p className="text-red-500 text-sm mt-1">{errors.partnerWilling}</p>}
            </div>

            {/* Story */}
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tell us your story *
              </label>
              <textarea
                name="story"
                value={formData.story}
                onChange={handleChange}
                rows={6}
                placeholder="Tell us what happened. How did you find out? What's the situation?"
                className={`w-full px-4 py-3 border rounded-lg outline-none transition-all resize-none ${
                  errors.story ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-500'
                } focus:ring-2 focus:border-red-500`}
              />
              {errors.story && <p className="text-red-500 text-sm mt-1">{errors.story}</p>}
              <p className="text-gray-400 text-sm mt-1">
                {formData.story.length}/500 characters (minimum 50)
              </p>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Submit Your Story
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default SubmitStoryPage;
